/**
 * TextField Component
 *
 * A styled text input component with consistent design system styling.
 */

import React from 'react';
import { TextField as MuiTextField, InputAdornment, Box, Typography } from '@mui/material';
import type { TextFieldProps as MuiTextFieldProps, SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface TextFieldProps {
    /** Field label */
    label: string;
    /** Current value */
    value: string;
    /** Change handler */
    onChange: (value: string) => void;
    /** Placeholder text */
    placeholder?: string;
    /** Helper text displayed below the field */
    helperText?: string;
    /** Error state */
    error?: boolean;
    /** Error message */
    errorMessage?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Whether the field is required */
    required?: boolean;
    /** Whether the field is read-only */
    readOnly?: boolean;
    /** Field type */
    type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
    /** Field size */
    size?: 'small' | 'medium';
    /** Full width */
    fullWidth?: boolean;
    /** Start adornment (icon/text) */
    startAdornment?: React.ReactNode;
    /** End adornment (icon/text) */
    endAdornment?: React.ReactNode;
    /** Multiline mode */
    multiline?: boolean;
    /** Number of rows for multiline */
    rows?: number;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const TextField: React.FC<TextFieldProps> = ({
    label,
    value,
    onChange,
    placeholder,
    helperText,
    error = false,
    errorMessage,
    disabled = false,
    required = false,
    readOnly = false,
    type = 'text',
    size = 'small',
    fullWidth = true,
    startAdornment,
    endAdornment,
    multiline = false,
    rows,
    sx,
}) => {
    const displayError = error || Boolean(errorMessage);
    const displayHelperText = errorMessage || helperText;

    return (
        <Box sx={sx}>
            {/* Standalone Label (omitted entirely when label is empty, e.g. rows with an external label) */}
            {label && (
                <Typography
                    component='label'
                    sx={{
                        display: 'block',
                        fontSize: typographyTokens.fontSize.sm,
                        fontWeight: typographyTokens.fontWeight.medium,
                        color: displayError ? colors.error.main : colors.neutral[500],
                        mb: '6px',
                    }}>
                    {label}
                    {required && <span style={{ color: colors.error.main, marginLeft: 4 }}>*</span>}
                </Typography>
            )}

            <MuiTextField
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                error={displayError}
                helperText={displayHelperText}
                disabled={disabled}
                type={type}
                size={size}
                fullWidth={fullWidth}
                multiline={multiline}
                rows={rows}
                InputProps={{
                    readOnly,
                    startAdornment: startAdornment ? (
                        <InputAdornment position='start'>{startAdornment}</InputAdornment>
                    ) : undefined,
                    endAdornment: endAdornment ? (
                        <InputAdornment position='end'>{endAdornment}</InputAdornment>
                    ) : undefined,
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        fontSize: typographyTokens.fontSize.sm,
                        color: colors.neutral[800],
                        backgroundColor: readOnly ? colors.neutral[50] : colors.neutral[0],
                        height: multiline ? 'auto' : 40,
                        alignItems: multiline ? 'flex-start' : 'center',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: displayError ? colors.error.main : colors.neutral[300],
                            borderRadius: `${radii.md}px`,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: displayError ? colors.error.dark : colors.neutral[400],
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: displayError ? colors.error.main : colors.primary[500],
                            borderWidth: 2,
                        },
                    },
                    '& .MuiFormHelperText-root': {
                        color: displayError ? colors.error.main : colors.neutral[500],
                        fontSize: typographyTokens.fontSize.xs,
                        mt: 0.5,
                    },
                    '& .MuiInputBase-input::placeholder': {
                        color: colors.neutral[500],
                        opacity: 1,
                    },
                }}
            />
        </Box>
    );
};

export default TextField;
